<template>
    <div style="height: 100%">
        <div id="main" v-show="!rotaAninhada">
            <container-loader v-if="isLoading && !$store.getters.isLoading" />
            <div class="page">
                <div id="vertical-flow">
                    <!-- TÍTULO -->
                    <div class="listing-header">
                        <div class="title-breadcrumb">
                            <h1 class="title">
                                {{ $t('prestacao-contas') }}
                            </h1>
                            <nav class="breadcrumb" aria-label="breadcrumbs">
                                <ul>
                                    <li>
                                        <a class="a-inativo">{{ $t('despesas') }}</a>
                                        <a class="a-inativo" aria-current="page">
                                            {{ $t('prestacao-contas') }}
                                        </a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                        <div class="header-buttons">
                            <button
                                v-if="permissao.create"
                                class="save-btn"
                                type="button"
                                tag="button"
                                @click="$router.push({ name: 'des-acerto-despesa-new' })"
                            >
                                {{ $t('adicionar') }}
                            </button>
                        </div>
                    </div>

                    <!-- PESQUISA E FILTROS -->
                    <div class="filters-bar mb-3">
                        <div class="filters-bar-form form-columns-fit">
                            <div class="columns">
                                <div class="column is-4">
                                    <search-bar ref="search" :onSearch="debouncedOnSearch" />
                                </div>
                                <!-- Data Inicio-->
                                <div class="column is-4">
                                    <verified-date-picker
                                        dateType="startDate"
                                        :value="datainicial"
                                        :endDate="datafinal"
                                        title="data-inicio"
                                        @input="changeStartDate"
                                        :showClean="false"
                                        required
                                        :invalid="datainicial == null"
                                    />
                                </div>
                                <!-- Data Fim-->
                                <div class="column is-4">
                                    <verified-date-picker
                                        dateType="endDate"
                                        :value="datafinal"
                                        :startDate="datainicial"
                                        title="data-fim"
                                        @input="changeEndDate"
                                        :showClean="false"
                                        required
                                        :invalid="datafinal == null"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="filters-bar-actions">
                            <b-button
                                type="is-primary"
                                icon-right="filter"
                                @click="showFiltros = !showFiltros"
                                v-tippy 
                                :title="$t('filtros')"
                            />
                        </div>
                    </div>

                    <!-- Filtro lateral -->
                    <div
                        ref="listaFiltros"
                        class="filtros-right"
                        :class="{ 'hide-filtros-right': !showFiltros }"
                        v-show="!isLoading"
                    >
                        <div class="filtros-container form-columns-fit">
                            <div class="columns is-multiline">
                                <div class="column is-12">
                                    <vss-select
                                        :options="situacaoSelect"
                                        v-model="situacaoSelected"
                                        mlLabels
                                        title="situacao"
                                        clearable
                                        @input="onCleanSituacao"
                                    />
                                </div>
                                <div class="column is-12">
                                    <vss-select
                                        title="empresa"
                                        placeholder="selecione"
                                        :options="empresaSelect"
                                        v-model="empresaSelected"
                                        :loading="empresaLoading"
                                        @search="carregarEmpresa"
                                        multiple
                                        searchable
                                        clearable
                                        :closeOnSelect="false"
                                    />
                                </div>
                                <div class="column is-12">
                                    <vss-select
                                        title="usuario"
                                        placeholder="selecione"
                                        v-model="usuarioSelected"
                                        :options="usuarioSelect"
                                        :loading="usuarioLoading"
                                        @search="carregarUsuario"
                                        label="nome"
                                        multiple
                                        searchable
                                        clearable
                                        :closeOnSelect="false"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="filtros-footer">
                            <div class="columns is-mobile">
                                <div class="column">
                                    <b-button
                                        type="is-warning is-silver"
                                        icon-right="close"
                                        @click="showFiltros = !showFiltros"
                                        v-tippy 
                                        :title="$t('fechar')"
                                    />
                                </div>
                                <div class="column">
                                    <b-button
                                        type="is-warning is-silver"
                                        icon-right="backspace"
                                        @click="onCleanFilters()"
                                        class="float-right"
                                    >
                                        Limpar
                                    </b-button>
                                </div>
                                <div class="column">
                                    <b-button
                                        type="is-primary"
                                        icon-right="filter"
                                        @click="timeoutCarregarDados()"
                                        class="float-right"
                                    >
                                        Filtrar
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- TABELA DE DESPESAS -->
                    <div class="registry-wrapper">
                        <empty-table-message v-if="isEmpty" />
                        <b-table
                            v-else
                            :data="acertoDespesaList"
                            class="data-table"
                            backend-sorting
                            @sort="sortTable"
                        >
                            <template v-for="(f, i) in fields">
                                <b-table-column
                                    :key="i"
                                    :field="f.key"
                                    :label="f.key"
                                    :width="f.width"
                                    :sortable="f.sortable"
                                >
                                    <!-- Cabeçalho da listagem -->
                                    <template v-slot:header="{ column }">
                                        <span v-if="column.label == 'codigo'">
                                            {{ $t('codigo') }}
                                            <b-icon
                                                v-if="
                                                    filters.column == 'iddes_acerto_despesaerperp'
                                                "
                                                :icon="`arrow-${
                                                    filters.order == 'asc' ? 'up' : 'down'
                                                }`"
                                                size="is-small"
                                            />
                                            <div class="table-sep" />
                                            {{ $t('descricao') }}
                                        </span>
                                        <span v-else-if="column.label == 'valor-total-itens'">
                                            {{ $t('valor-total-itens') }}
                                            <div class="table-sep" />
                                            {{ $t('valor-aprovado') }}
                                        </span>
                                        <span v-else-if="column.label == 'empresa'">
                                            {{ $t('empresa') }}
                                            <div class="table-sep" />
                                            {{ $t('colaborador') }}
                                        </span>
                                        <span v-else-if="column.label == 'data-inicio'">
                                            {{ $t('data-inicio') }}
                                            <b-icon
                                                v-if="filters.column == 'data-inicio'"
                                                :icon="`arrow-${
                                                    filters.order == 'asc' ? 'up' : 'down'
                                                }`"
                                                size="is-small"
                                            />
                                            <div class="table-sep" />
                                            {{ $t('data-finalizacao') }}
                                        </span>
                                        <span v-else>
                                            {{ column.label ? $t(column.label) : '' }}
                                        </span>
                                    </template>
                                    <!-- Dados da listagem -->
                                    <template v-slot="props">
                                        <div v-if="f.key == 'codigo'">
                                            <strong>
                                                <router-link
                                                    :to="{
                                                        name: 'des-acerto-despesa-edit',
                                                        params: { id: props.row.id },
                                                    }"
                                                >
                                                    {{ props.row[f.key] }}
                                                </router-link>
                                            </strong>
                                            <div class="table-sep" />
                                            <router-link
                                                :to="{
                                                    name: 'des-acerto-despesa-edit',
                                                    params: { id: props.row.id },
                                                }"
                                            >
                                                {{ props.row.descricao }}
                                            </router-link>
                                        </div>

                                        <div v-else-if="f.key == 'valor-total-itens'">
                                            <span v-if="props.row[f.key] == null">---</span>
                                            <span v-else>
                                                {{ props.row[f.key] | decimal(null, true) }}
                                            </span>
                                            <div class="table-sep" />
                                            <span
                                                v-if="
                                                    props.row['situacao'] == 1 ||
                                                    props.row['situacao'] == 2
                                                "
                                            >
                                                {{
                                                    props.row['valor-aprovado']
                                                        | decimal(null, true)
                                                }}
                                            </span>
                                            <span v-else>---</span>
                                        </div>

                                        <div v-else-if="f.key == 'data-inicio'">
                                            {{ props.row[f.key] | moment('L') }}
                                            <div class="table-sep" />
                                            <span v-if="props.row['data-finalizacao'] == null"
                                                >---</span
                                            >
                                            <span v-else>
                                                {{ props.row['data-finalizacao'] | moment('L LT') }}
                                            </span>
                                        </div>

                                        <div v-else-if="f.key == 'empresa'">
                                            {{ props.row[f.key] }}
                                            <div class="table-sep" />
                                            {{ props.row['colaborador'] }}
                                        </div>

                                        <div v-else-if="f.key == 'situacao'">
                                            <b-tag
                                                class="table-tag table-tag-full-width"
                                                :type="isStatusButtonType(props.row[f.key])"
                                                size="is-medium"
                                            >
                                                <span v-if="props.row[f.key] == 0">
                                                    {{ $t('reprovado') }}
                                                </span>
                                                <span v-else-if="props.row[f.key] == 1">
                                                    {{ $t('aprovado') }}
                                                </span>
                                                <span v-else-if="props.row[f.key] == 2">
                                                    {{ $t('aprovado-parcial') }}
                                                </span>
                                                <span v-else-if="props.row[f.key] == 3">
                                                    {{ $t('em-criacao') }}
                                                </span>
                                                <span v-else-if="props.row[f.key] == 4">
                                                    {{ $t('aguardando-aprovacao') }}
                                                </span>
                                                <span v-else-if="props.row[f.key] == 5">
                                                    {{ $t('aguardando-revisao') }}
                                                </span>
                                            </b-tag>
                                        </div>

                                        <div v-else-if="f.key == 'excluir'">
                                            <b-button
                                                v-if="props.row.situacao == 3"
                                                class="action-button"
                                                type="is-danger"
                                                icon-right="delete"
                                                @click="promptDeleteItem(props.row)"
                                                v-tippy 
                                                :title="$t('excluir')"
                                            />
                                        </div>

                                        <div v-else>
                                            {{ props.row[f.key] }}
                                        </div>
                                    </template>
                                </b-table-column>
                            </template>
                        </b-table>
                        <div class="totalizadores">
                            <div class="totalizador">
                                <div>
                                    <b>{{ $t('valor-total-itens') }}: </b>
                                    {{ getDecimal(valorTotalItens, null, true) }}
                                </div>
                            </div>
                            <div class="totalizador">
                                <div>
                                    <b>{{ $t('valor-total-aprovado') }}: </b>
                                    {{ getDecimal(valorTotalAprovado, null, true) }}
                                </div>
                            </div>
                        </div>
                        <PaginationViewer
                            class="mb-0"
                            :resetPagination="pagination.resetPagination"
                            :totalItems="pagination.totalRows"
                            :perPageOptions="pagination.perPageOptions"
                            :optionStarted="pagination.optionStarted"
                            @change="updatePagination"
                        />
                    </div>
                </div>
            </div>
        </div>
        <router-view />
    </div>
</template>

<script>
import AcertoDespesaService from '@/services/despesas/AcertoDespesaService';
import EmpresaService from '@/services/vendas/empresa/EmpresaService';
import GerenciarUsuariosService from '@/services/conta/GerenciarUsuariosService';
// COMPONENTS
import ContainerLoader from '@/components/content/loader/ContainerLoader';

export default {
    beforeRouteUpdate(to, from, next) {
        if (to.name.includes('des-acerto-despesa') && to.name != 'des-acerto-despesa-list') {
            this.rotaAninhada = true;
            next();
        } else {
            this.rotaAninhada = false;
            this.carregarDados(this.pagination.currentPage);
            next();
        }
    },
    components: {
        ContainerLoader,
    },
    data() {
        return {
            rotaAninhada: false,
            service: new AcertoDespesaService(this.$http, this.$router),
            empresaService: new EmpresaService(this.$http, this.$router),
            gerenciarUsuariosService: new GerenciarUsuariosService(this.$http, this.$router),
            fields: [
                { key: 'codigo', width: null, sortable: true },
                { key: 'data-inicio', width: null, sortable: true },
                { key: 'valor-total-itens', width: null, sortable: false },
                { key: 'empresa', width: null, sortable: false },
                { key: 'situacao', width: 210, sortable: false },
                { key: 'excluir', width: null, sortable: false },
            ],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            isLoading: false,
            isEmpty: false,
            permissao: {},
            // dados
            acertoDespesaList: [],
            situacaoSelect: [
                { label: 'todos', id: 6 },
                { label: 'reprovado', id: 0 },
                { label: 'aprovado', id: 1 },
                { label: 'aprovado-parcial', id: 2 },
                { label: 'em-criacao', id: 3 },
                { label: 'aguardando-aprovacao', id: 4 },
                { label: 'aguardando-revisao', id: 5 },
            ],
            situacaoSelected: { label: 'todos', id: 6 },
            empresaSelect: [],
            empresaSelected: [],
            empresaLoading: false,
            usuarioSelect: [],
            usuarioSelected: [],
            usuarioLoading: false,
            valorTotalAprovado: 0,
            valorTotalItens: 0,
            // filtros
            debouncedOnSearch: null,
            filters: {
                queryFilter: '.*',
                datainicial: null,
                datafinal: null,
                column: 'datahorainc',
                order: 'desc',
                situacao: 2,
                empresaList: [],
                usuarioList: [],
                currentPage: 1,
                perPage: 10,
            },
            datainicial: (() => {
                let trintaDiasAtras = new Date();
                trintaDiasAtras.setMonth(trintaDiasAtras.getMonth() - 43);
                return trintaDiasAtras;
            })(),
            datafinal: new Date(),
            showFiltros: false,
            limparBusca: false,
        };
    },
    created() {
        this.rotaAninhada =
            this.$route.name.includes('des-acerto-despesa') &&
            this.$route.name != 'des-acerto-despesa-list';

        this.pagination.perPage = this.pagination.perPageOptions[this.pagination.optionStarted];

        this.permissao = this.hasPermissionAccess(
            'despesas',
            'prestacao-contas',
            'des-acerto-despesa'
        );

        if (!this.permissao.read) {
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'prestacao-contas' },
            });
        } else {
            this.debouncedOnSearch = this.debounce(this.onSearch, 500);
            this.carregarDados();
        }
    },
    methods: {
        // Recebe o $emit do VerifiedDatePicker, adiciona o valor na variavel local que retorna para a prop do VerifiedDatePiker
        changeStartDate(date) {
            if (date.getTime() != this.datainicial.getTime()) {
                this.datainicial = date;
                if (!this.isNull(this.datainicial)) this.carregarDados();
            }
        },
        changeEndDate(date) {
            if (date.getTime() != this.datafinal.getTime()) {
                this.datafinal = date;
                if (!this.isNull(this.datafinal)) this.carregarDados();
            }
        },
        sortTable(field, order) {
            this.filters.column = field;
            this.filters.order = order;
            this.carregarDados();
        },
        onCleanSituacao(val) {
            if (!val) this.situacaoSelected = this.situacaoSelect[0];
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        onSearch(value) {
            this.filters.queryFilter = this.removeToSearch(value);
            this.pagination.currentPage = 1;
            this.carregarDados();
        },
        // Carregamento de prestacao-contas
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;

            this.isLoading = true;

            this.fillFilter(currentPage);
        },
        buscarTotais() {
            this.service
                .findTotais(this.filters)
                .then((value) => {
                    (this.valorTotalAprovado = value.content.valortotalaprovado),
                        (this.valorTotalItens = value.content.valortotalitens);
                })
                .catch((e) => {
                    this.isEmpty = true;
                    this.$root.showMessage('error', 'msg-erro-load');
                });
        },
        buscarAcertoDespesa() {
            this.service
                .findAll(this.filters)
                .then(this.mapItems)
                .then((value) => {
                    this.acertoDespesaList = value;
                    this.isEmpty = this.acertoDespesaList.length <= 0;
                })
                .catch((e) => {
                    this.isEmpty = true;
                    this.$root.showMessage('error', 'msg-erro-load');
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.pagination.resetPagination = false;
                });
        },
        mapItems(body) {
            this.pagination.totalRows = body.totalElements;
            return body.content.map((m) => {
                return {
                    uuid: m.uuid,
                    id: m.iddes_acerto_despesa,
                    codigo: m.iddes_acerto_despesaerp,
                    'data-inicio': m.datainicio,
                    'data-finalizacao': m.datahorafinalizacao,
                    'valor-total-itens': m.valortotalitens,
                    'valor-aprovado': m.valortotal,
                    empresa: m.empresa.nome,
                    colaborador: m.usuario.nome,
                    descricao: m.descricao,
                    situacao: m.situacao,
                };
            });
        },
        carregarEmpresa(query) {
            query = this.isNull(query) ? '.*' : this.removeToSearch(query);

            this.empresasLoading = true;
            this.empresaService
                .findAllComboSituacao(4, query)
                .then((body) => {
                    if (body.content) {
                        this.empresaSelect = body.content.map((m) => {
                            return {
                                id: m.idempresa,
                                label:
                                    m.replicapda == 2
                                        ? 'Inativo | ' + m.nome + ' - ' + m.rsocial
                                        : m.nome + ' - ' + m.rsocial,
                                replicapda: m.replicapda,
                            };
                        });
                    }
                })
                .catch((e) => {
                    console.error('Erro ao carregar empresas.');
                })
                .finally((_) => {
                    this.empresasLoading = false;
                });
        },
        carregarUsuario(query) {
            query = this.isNull(query) ? '.*' : this.removeToSearch(query);

            this.usuarioLoading = true;
            this.gerenciarUsuariosService
                .findAllComboSituacao(2, query)
                .then((body) => {
                    return body.content.map((m) => {
                        return {
                            id: m.idusuario,
                            idusuario: m.idusuario,
                            nome: m.situacao != 'A' ? 'Inativo | ' + m.nome : m.nome,
                            situacao: m.situacao,
                        };
                    });
                })
                .then((content) => {
                    this.usuarioSelect = content;
                })
                .catch((e) => {
                    console.error(`Erro ao realizar pesquisa. Termo: [${query}]`);
                })
                .finally((_) => {
                    this.usuarioLoading = false;
                });
        },
        fillFilter(currentPage = 1) {
            this.filters.situacao = this.situacaoSelected != null ? this.situacaoSelected.id : null;
            this.filters.empresaList = this.empresaSelected
                ? this.empresaSelected.map((e) => e.id)
                : [];
            this.filters.usuarioList = this.usuarioSelected
                ? this.usuarioSelected.map((e) => e.id)
                : [];

            this.filters.perPage = this.pagination.perPage;
            this.filters.currentPage = currentPage;
            this.filters.queryFilter = this.filters.queryFilter || '.*';

            this.filters.datainicial = this.datainicial
                ? this.$moment(this.datainicial).format('YYYY-MM-DD')
                : null;
            this.filters.datafinal = this.datafinal
                ? this.$moment(this.datafinal).format('YYYY-MM-DD')
                : null;

            if (this.filters.column == 'codigo') {
                this.filters.column = 'iddes_acerto_despesaerperp';
            }

            this.buscarTotais();
            this.buscarAcertoDespesa();
        },
        onCleanFilters() {
            this.situacaoSelected = this.situacaoSelect[0];
            this.empresaSelected = [];
            this.usuarioSelected = [];
            this.filters.search = '';
            this.datainicial = (() => {
                let trintaDiasAtras = new Date();
                trintaDiasAtras.setMonth(trintaDiasAtras.getMonth() - 3);
                return trintaDiasAtras;
            })();
            this.datafinal = new Date();
            this.$refs.search.cleanSearch();

            this.timeoutCarregarDados();
        },
        timeoutCarregarDados(time = 600) {
            this.showFiltros = false;
            setTimeout(() => {
                this.carregarDados();
            }, time);
        },
        isStatusButtonType(status) {
            if (status == 0) return 'is-danger';
            else if (status == 1) return 'is-success';
            else if (status == 2) return 'is-success';
            else if (status == 3) return 'is-primary';
            else if (status == 4) return 'is-warning';
            else if (status == 5) return 'is-primary';
        },
        promptDeleteItem(row) {
            this.$root.confirm({
                message: this.$t('msg-confirma-remover-n', 
                    { n: row.descricao + ' - ' + this.getDecimal(
                        row['valor-total-itens'], null, true
                    ) }
                ),
                onConfirm: (_) => this.remove(row),
            });
        },
        remove(row) {
            this.isLoading = true;

            const prestacaoConta =
                row.descricao + ' - ' + this.getDecimal(row['valor-total-itens'], null, true);

            this.service
                .remove(row.uuid)
                .then((r) => {
                    let msg = this.$t('msg-sucesso-remover-n', { n: prestacaoConta });

                    this.$root.showMessage('success', msg, true, false);
                })
                .catch((e) => {
                    let msg = this.$t('msg-erro-remover-n', { n: prestacaoConta });

                    this.$root.showMessage('error', msg, true, false);
                })
                .finally((_) => {
                    this.isLoading = false;
                    this.carregarDados(this.pagination.currentPage);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: 100%;
    .page {
        position: relative;
        padding-left: 0;

        // Barra de filtro
        .filters-bar {
            display: flex;
            .filters-bar-form {
                flex-grow: 1;
            }
            .filters-bar-actions {
                display: flex;
                padding-bottom: 2px;
                align-items: stretch;
            }

            @media screen and (min-width: 851px) {
                .filters-bar-actions {
                    align-self: flex-end;
                    margin-left: 10px;
                    margin-bottom: 12px;
                }
            }
            @media screen and (max-width: 850px) {
                flex-direction: column;
                .filters-bar-actions {
                    align-self: center;
                    margin-top: 10px;
                }
            }
        }
        .label-situacao {
            position: absolute;
            bottom: 5px;
            top: 5px;
            display: flex;

            .bar {
                border-radius: 100px;
                width: 5px;
                min-height: 25px;
                height: 100%;
                &.bar-a-vencer {
                    background-color: #84bd42;
                }
                &.bar-vencido {
                    background-color: #fd5858;
                }
            }
        }
    }
    // Filtro lateral
    $width: 400px;
    .filtros-right {
        z-index: 100;
        padding: 15px;
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
        background-color: $darker-bg-color;
        width: $width;
        height: 80%;
        position: fixed;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        border-top: solid 1px #d9d9d9;
        border-left: solid 1px #d9d9d9;
        border-bottom: solid 1px #d9d9d9;
        transition: 0.6s;
        -webkit-box-shadow: 0px 0px 25px 0px rgba(138, 138, 138, 0.8);
        box-shadow: 0px 0px 25px 0px rgba(138, 138, 138, 0.8);

        .filtros-container {
            margin-bottom: 60px;
        }

        .filtros-footer {
            border-bottom-left-radius: 5px;
            background-color: #fff;
            position: absolute;
            padding: 10px;
            bottom: 0;
            left: 0;
            right: 0;
            -webkit-box-shadow: 0px 0px 7px 0px rgba(153, 153, 153, 0.4);
            box-shadow: 0px 0px 7px 0px rgba(153, 153, 153, 0.4);

            .is-silver {
                color: $semi-title-color;
                box-shadow: inset 0px 0px 2px $dark-gray-color-opacity;
                @include exclude-button();
            }
        }
    }
    .hide-filtros-right {
        margin-right: -$width;
    }
    @media screen and (min-height: 850px) {
        .filtros-right {
            height: 50%;
        }
    }
    @media screen and (max-width: 640px) {
        $width: 95%;
        .filtros-right {
            width: $width;
        }
        .hide-filtros-right {
            margin-right: -$width;
        }
    }
    .totalizadores {
        display: flex;
        margin-top: 30px;
        justify-content: space-between;
        border-top: 2px solid $table-border;

        .totalizador {
            margin: 10px 14px;
            margin-left: 10%;
            margin-right: 10%;
            color: $darker-gray-color;
        }
    }
}
</style>
